import { makeResettableSignal, timeout } from "./schedulingUtils";

// Waits until a condition has been true for at least minTrueDurationMs.
// State invariants: toggleSignal is triggered iff currentValue is true; lastTrue is set iff currentValue is true
export const createConditionWaiter = ({
  conditionSubscriber,
  minTrueDurationMs = 0,
  initialValue = false,
  initialChangedAtMs = 0,
}: {
  conditionSubscriber: (
    listener: (value: boolean) => void,
  ) => (() => void) | void;
  minTrueDurationMs?: number;
  initialValue?: boolean;
  initialChangedAtMs?: number | null;
}) => {
  let currentValue = initialValue;
  let lastTrue: number | null = currentValue ? initialChangedAtMs : null;
  const toggleSignal = makeResettableSignal(currentValue);

  const unsubscribe = conditionSubscriber((value) => {
    if (value !== currentValue) {
      currentValue = value;
      if (value === true) {
        toggleSignal.trigger(); // Unblock any waiting calls
        lastTrue = Date.now();
      } else {
        toggleSignal.reset(); // Next wait() will block until true again
        lastTrue = null;
      }
    }
  });

  const waitUntilConditionTrue = async () => {
    while (true) {
      await toggleSignal.wait(); // Block until condition becomes true
      if (currentValue && lastTrue !== null) {
        const duration = Date.now() - lastTrue;
        if (duration >= minTrueDurationMs) {
          break; // Condition has been true long enough
        }
        await timeout(minTrueDurationMs - duration); // Wait remaining debounce time
        // If condition went false during timeout, toggleSignal was reset, so loop restarts
      }
    }
  };

  const cleanup = () => {
    unsubscribe?.();
  };
  return {
    waitUntilConditionTrue,
    cleanup,
  };
};

/**
 * A sample conditionSubscriber, where it polls the condition every intervalMs and calls the listener with the current value.
 */
export const pollingCheckListener =
  (check: () => boolean, intervalMs: number = 1000) =>
  (listener: (value: boolean) => void) => {
    const currentValue = check();
    listener(currentValue);
    setTimeout(() => {
      pollingCheckListener(check, intervalMs)(listener);
    }, intervalMs);
  };
